/**
 * L'âge d'un candidat, à partir de sa date de naissance.
 *
 * 📅 Kayla veut savoir son âge (18-08-2026) ; on lui demande sa DATE DE
 * NAISSANCE. Un âge saisi est faux un an plus tard et personne ne revient le
 * corriger ; une date reste vraie et l'âge se recalcule à chaque lecture.
 *
 * ⛔ FONCTION À PART, et ce n'est pas de la décoration. Elle vivait dans le
 * composant, où elle n'était atteignable au banc qu'en tapant dans un
 * `<input type="date">` — et happy-dom **vide** une valeur hors de l'attribut
 * `max`, là où Chrome la garde en la marquant seulement invalide. Le test qui
 * croyait éprouver « on refuse un mineur » éprouvait en réalité « le champ est
 * vide », et il serait resté vert sur un calcul d'âge cassé. Sortie d'ici,
 * elle se teste pour ce qu'elle est : de l'arithmétique.
 *
 * 🪤 Et cette arithmétique a un piège. `annee - annee` se trompe pour tous ceux
 * qui n'ont pas encore eu leur anniversaire cette année — la moitié des gens un
 * jour donné. Sans le terme correctif, un candidat de 18 ans né en décembre se
 * ferait refuser. Le serveur fait le MÊME calcul (`_age_revolu`, côté Cyrano) :
 * les deux doivent rester d'accord, sinon le formulaire accepte ce que la route
 * refuse et le candidat se retrouve devant une erreur qu'il ne peut pas lever.
 */

/**
 * L'âge révolu, ou `null` si la date n'est pas une date ISO complète.
 *
 * `null` veut dire « pas renseigné », jamais « zéro » — un âge manquant et un
 * âge de zéro ne se traitent pas pareil ([[doctrine-absence-info]]).
 */
export function ageRevolu(dateISO: string, maintenant?: Date): number | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dateISO)) return null;
  const [a, m, j] = dateISO.split("-").map(Number) as [number, number, number];
  // Un mois hors [1,12] ou un jour hors [1,31] est une saisie, pas une date.
  if (m < 1 || m > 12 || j < 1 || j > 31) return null;

  const d = maintenant ?? new Date();
  const moisCourant = d.getMonth() + 1;
  const pasEncoreFete =
    moisCourant < m || (moisCourant === m && d.getDate() < j);
  return d.getFullYear() - a - (pasEncoreFete ? 1 : 0);
}

/**
 * La dernière date de naissance qui donne `ageMinimum` ans aujourd'hui — sert
 * de `max` au sélecteur natif.
 *
 * Confort, pas sécurité : le champ reste saisissable au clavier dans un vrai
 * navigateur, et c'est le serveur qui tranche (la route du profil est publique,
 * jeton dans l'URL).
 */
export function naissanceLaPlusTardive(ageMinimum: number, maintenant?: Date): string {
  const d = new Date(maintenant ?? new Date());
  d.setFullYear(d.getFullYear() - ageMinimum);
  // 🪤 PAS `toISOString()`. `setFullYear` travaille en heure LOCALE, mais
  // `toISOString` rend la date en UTC : à l'ouest de Greenwich, passé
  // (24 − décalage) heures locales, elle renvoie DEMAIN. La borne partait
  // alors un jour trop tard et le sélecteur laissait choisir une date qui ne
  // donne que 17 ans. Mesuré le 18-08-2026 depuis un poste en UTC−3 : le test
  // des 18 ans virait au rouge chaque soir à partir de 21 h locales, et
  // repassait au vert le matin. On lit donc les composants LOCAUX, les mêmes
  // que ceux que `setFullYear` vient d'écrire.
  const deuxChiffres = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${deuxChiffres(d.getMonth() + 1)}-${deuxChiffres(d.getDate())}`;
}
